import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import Layout from '../components/Layout';
import ReportsTab from '../components/ReportsTab';
import { FaBuilding, FaUniversity, FaClipboardList, FaUserShield, FaChartBar, FaCompass } from 'react-icons/fa';
import SubDistrictModal from '../components/modals/SubDistrictModal';
import ClubModal from '../components/modals/ClubModal';
import ProjectModal from '../components/modals/ProjectModal';
import AdminModal from '../components/modals/AdminModal';
import SubDistrictsTable from '../components/tables/SubDistrictsTable';
import ClubsTable from '../components/tables/ClubsTable';
import ProjectsTable from '../components/tables/ProjectsTable';
import AdminsTable from '../components/tables/AdminsTable';
import ConfirmationModal from '../components/ConfirmationModal';
import DiscoverTab from '../components/dashboard/DiscoverTab';

const SuperAdminDashboard = () => {
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('sub-districts');
    const [subDistricts, setSubDistricts] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [projects, setProjects] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [formData, setFormData] = useState({});
    const [selectedSubDistrict, setSelectedSubDistrict] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, id: null });
    const navigate = useNavigate();

    const tabs = [
        { id: 'sub-districts', label: 'Manage Sub-Districts', icon: <FaBuilding /> },
        { id: 'clubs', label: 'Manage Clubs', icon: <FaUniversity /> },
        { id: 'projects', label: 'Manage Projects', icon: <FaClipboardList /> },
        { id: 'discover', label: 'Discover', icon: <FaCompass /> },
        { id: 'admins', label: 'Manage Admins', icon: <FaUserShield /> },
        { id: 'reports', label: 'Reports', icon: <FaChartBar /> },
    ];

    const fetchSubDistricts = useCallback(async () => {
        const data = await adminApi.getSubDistricts();
        if (data.success) setSubDistricts(data.data.sub_districts);
    }, []);

    const fetchClubs = useCallback(async () => {
        const data = await adminApi.getClubs();
        if (data.success) setClubs(data.data.clubs);
    }, []);

    const fetchProjects = useCallback(async () => {
        const data = await adminApi.getProjects();
        if (data.success) setProjects(data.data.projects);
    }, []);

    const fetchAdmins = useCallback(async () => {
        const data = await adminApi.getAdmins();
        if (data.success) setAdmins(data.data.admins);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            if (activeTab === 'sub-districts') await fetchSubDistricts();
            else if (activeTab === 'clubs') await fetchClubs();
            else if (activeTab === 'projects') await fetchProjects();
            else if (activeTab === 'admins') await fetchAdmins();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, fetchSubDistricts, fetchClubs, fetchProjects, fetchAdmins]);

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            navigate('/login');
            return;
        }
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'super_admin') {
            navigate('/login');
            return;
        }
        setUser(userData);
        fetchData();

        // Also pre-fetch necessary dropdown data if needed when switching tabs?
        // But existing logic fetches on tab switch. 
        // We might need clubs list when creating admin, or subdistricts when creating club.
        // It's safer to fetch them all initially or ensure they are present.
        // For now, let's just stick to the current lazy loading but maybe eager load list dependencies.
        if (activeTab === 'clubs') fetchSubDistricts();
        if (activeTab === 'admins' || activeTab === 'projects') {
            fetchSubDistricts();
            fetchClubs();
        }

    }, [navigate, fetchData, activeTab, fetchSubDistricts, fetchClubs]);

    const handleSubDistrictClick = async (subDistrict) => {
        console.log('Clicked sub-district:', subDistrict);
        setSelectedSubDistrict(subDistrict);
        setActiveTab('clubs');
    };

    const openModal = (type, data = {}) => {
        // Ensure dependecies are loaded
        if (type.includes('club') && subDistricts.length === 0) fetchSubDistricts();
        if ((type.includes('admin') || type.includes('project')) && clubs.length === 0) fetchClubs();
        if (type.includes('admin') && subDistricts.length === 0) fetchSubDistricts();

        setModalType(type);
        setFormData(data);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormData({});
        setModalType('');
    };

    const handleModalSubmit = async (data) => {
        setLoading(true);
        try {
            let res;
            if (modalType === 'create-sub-district' || modalType === 'edit-sub-district') {
                res = data.id ? await adminApi.updateSubDistrict(data) : await adminApi.createSubDistrict(data);
            } else if (modalType === 'create-club' || modalType === 'edit-club') {
                res = modalType === 'edit-club' ? await adminApi.updateClub(data) : await adminApi.createClub(data);
            } else if (modalType === 'edit-project') {
                res = await adminApi.updateProject(data);
            } else if (modalType === 'create-admin' || modalType === 'edit-admin') {
                res = data.id ? await adminApi.updateAdmin(data) : await adminApi.createAdmin(data);
            }

            if (res.success) {
                toast.success('Success!');
                closeModal();
                fetchData();
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            toast.error('Error: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (type, id) => {
        setConfirmModal({
            isOpen: true,
            type,
            id,
            title: `Delete ${type.replace('-', ' ')}`,
            message: `Are you sure you want to delete this ${type.replace('-', ' ')}? This action cannot be undone.`
        });
    };

    const executeDelete = async () => {
        const { type, id } = confirmModal;
        setLoading(true);
        try {
            let res;
            if (type === 'sub-district') res = await adminApi.deleteSubDistrict(id);
            else if (type === 'club') res = await adminApi.deleteClub(id);
            else if (type === 'project') res = await adminApi.deleteProject(id);
            else if (type === 'admin') res = await adminApi.deleteAdmin(id);

            if (res && res.success) {
                fetchData();
                toast.success('Deleted successfully!');
                setConfirmModal({ isOpen: false, type: null, id: null });
            } else {
                toast.error(res?.message || 'Failed to delete');
            }
        } catch (error) {
            toast.error('Error: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (loading && !subDistricts.length && !clubs.length && !projects.length && !admins.length) return <div className="p-8 text-center text-gray-500">Loading...</div>;

        switch (activeTab) {
            case 'sub-districts':
                return (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <div>
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">Sub-Districts</h2>
                                <p className="text-sm text-gray-500 mt-1">Click on a sub-district to view its clubs</p>
                            </div>
                            <button
                                onClick={() => openModal('create-sub-district', { district_id: 1 })}
                                className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                            >
                                <span className="text-lg">+</span> Add Sub-District
                            </button>
                        </div>
                        <SubDistrictsTable
                            subDistricts={subDistricts}
                            onRowClick={handleSubDistrictClick}
                            onEdit={(sd) => { openModal('edit-sub-district', sd); }}
                            onDelete={(id) => handleDeleteClick('sub-district', id)}
                        />
                    </div>
                );

            case 'clubs':
                const filteredClubs = selectedSubDistrict
                    ? clubs.filter(club => String(club.sub_district_id) === String(selectedSubDistrict.id))
                    : clubs;

                return (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <div>
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">Clubs</h2>
                                {selectedSubDistrict && (
                                    <div className="flex items-center gap-2 mt-1">
                                        <p className="text-sm text-gray-500">
                                            Showing clubs in: <span className="font-semibold text-gray-700">{selectedSubDistrict.name}</span>
                                        </p>
                                        <button
                                            onClick={() => setSelectedSubDistrict(null)}
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                        >
                                            Clear filter
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => openModal('create-club')}
                                className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                            >
                                <span className="text-lg">+</span> Add Club
                            </button>
                        </div>

                        {filteredClubs.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
                                {selectedSubDistrict
                                    ? `No clubs found in ${selectedSubDistrict.name}`
                                    : 'No clubs available'
                                }
                            </div>
                        ) : (
                            <ClubsTable
                                clubs={filteredClubs}
                                onEdit={(club) => openModal('edit-club', club)}
                                onDelete={(id) => handleDeleteClick('club', id)}
                                showSubDistrict={!selectedSubDistrict}
                            />
                        )}
                    </div>
                );

            case 'projects':
                return (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">Projects</h2>
                        </div>
                        <ProjectsTable
                            projects={projects}
                            onEdit={(project) => openModal('edit-project', project)}
                            onDelete={(id) => handleDeleteClick('project', id)}
                        />
                    </div>
                );

            case 'admins':
                return (
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">Admins</h2>
                            <button
                                onClick={() => openModal('create-admin')}
                                className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                            >
                                <span className="text-lg">+</span> Add Admin
                            </button>
                        </div>
                        <AdminsTable
                            admins={admins}
                            onEdit={(admin) => openModal('edit-admin', admin)}
                            onDelete={(id) => handleDeleteClick('admin', id)}
                            showRole={true}
                            showSubDistrict={true}
                        />
                    </div>
                );

            case 'reports':
                return <ReportsTab user={user} />;
            case 'discover':
                return <DiscoverTab />;
            default:
                return null;
        }
    };

    return (
        <Layout user={user} activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs}>
            {renderContent()}

            {(modalType === 'create-sub-district' || modalType === 'edit-sub-district') && (
                <SubDistrictModal
                    isOpen={showModal}
                    onClose={closeModal}
                    onSubmit={handleModalSubmit}
                    result={formData}
                    loading={loading}
                />
            )}

            {(modalType === 'create-club' || modalType === 'edit-club') && (
                <ClubModal
                    isOpen={showModal}
                    onClose={closeModal}
                    onSubmit={handleModalSubmit}
                    result={formData}
                    loading={loading}
                    subDistricts={subDistricts}
                />
            )}

            {(modalType === 'create-admin' || modalType === 'edit-admin') && (
                <AdminModal
                    isOpen={showModal}
                    onClose={closeModal}
                    onSubmit={handleModalSubmit}
                    result={formData}
                    loading={loading}
                    clubs={clubs}
                    subDistricts={subDistricts}
                    showRoleSelection={true}
                />
            )}

            {(modalType === 'edit-project') && (
                <ProjectModal
                    isOpen={showModal}
                    onClose={closeModal}
                    onSubmit={handleModalSubmit}
                    result={formData}
                    loading={loading}
                    clubs={clubs}
                />
            )}

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                onConfirm={executeDelete}
                title={confirmModal.title}
                message={confirmModal.message}
                loading={loading}
            />
        </Layout>
    );
};

export default SuperAdminDashboard;
